import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Claude } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { ArrowRight, Mic, X, Bot, Link2, Lightbulb, MessageCircle, Hash, Download, Key, Coins } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
const STORAGE_KEY = 'orsay_whats_new_seen';
interface Feature {
  icon: ReactNode;
  titleKey: string;
  descriptionKey: string;
  href: string;
}
const CHANGELOG_BASE = 'https://docs.orsay.ai/en/changelog';
const ALL_FEATURES: Feature[] = [{
  icon: <Coins className="size-5" />,
  titleKey: 'whatsNew.items.perMessageBilling.title',
  descriptionKey: 'whatsNew.items.perMessageBilling.description',
  href: `${CHANGELOG_BASE}#per-message-billing`
}, {
  icon: <Key className="size-5" />,
  titleKey: 'whatsNew.items.byok.title',
  descriptionKey: 'whatsNew.items.byok.description',
  href: `${CHANGELOG_BASE}#bring-your-own-key`
}, {
  icon: <Mic className="size-5" />,
  titleKey: 'whatsNew.items.whatsappPersonalAudio.title',
  descriptionKey: 'whatsNew.items.whatsappPersonalAudio.description',
  href: `${CHANGELOG_BASE}#personal-whatsapp-audio`
}, {
  icon: <Claude className="size-5" />,
  titleKey: 'whatsNew.items.claudeMcp.title',
  descriptionKey: 'whatsNew.items.claudeMcp.description',
  href: `${CHANGELOG_BASE}#claude-mcp`
}, {
  icon: <Mic className="size-5" />,
  titleKey: 'whatsNew.items.voiceCloning.title',
  descriptionKey: 'whatsNew.items.voiceCloning.description',
  href: `${CHANGELOG_BASE}#voice-cloning`
}, {
  icon: <Bot className="size-5" />,
  titleKey: 'whatsNew.items.aiSequence.title',
  descriptionKey: 'whatsNew.items.aiSequence.description',
  href: `${CHANGELOG_BASE}#build-with-ai`
}, {
  icon: <Link2 className="size-5" />,
  titleKey: 'whatsNew.items.resources.title',
  descriptionKey: 'whatsNew.items.resources.description',
  href: `${CHANGELOG_BASE}#resources`
}, {
  icon: <Lightbulb className="size-5" />,
  titleKey: 'whatsNew.items.improvements.title',
  descriptionKey: 'whatsNew.items.improvements.description',
  href: `${CHANGELOG_BASE}#improvements`
}, {
  icon: <MessageCircle className="size-5" />,
  titleKey: 'whatsNew.items.whatsapp.title',
  descriptionKey: 'whatsNew.items.whatsapp.description',
  href: `${CHANGELOG_BASE}#whatsapp`
}, {
  icon: <Hash className="size-5" />,
  titleKey: 'whatsNew.items.keywordFlow.title',
  descriptionKey: 'whatsNew.items.keywordFlow.description',
  href: `${CHANGELOG_BASE}#instagram-keyword-flows`
}, {
  icon: <Download className="size-5" />,
  titleKey: 'whatsNew.items.analyticsExport.title',
  descriptionKey: 'whatsNew.items.analyticsExport.description',
  href: `${CHANGELOG_BASE}#analytics-export`
}, {
  icon: <Download className="size-5" />,
  titleKey: 'whatsNew.items.conversationsExport.title',
  descriptionKey: 'whatsNew.items.conversationsExport.description',
  href: `${CHANGELOG_BASE}#conversations-export`
}];

/**
 * Derived from ALL_FEATURES — adding, removing, renaming, or reordering
 * an entry automatically invalidates everyone's "seen" state, so the modal
 * re-appears for them on next visit. No manual bumping required.
 */
const WHATS_NEW_VERSION = ALL_FEATURES.map(f => f.titleKey).join('|');
interface WhatsNewModalProps {
  forceOpen?: boolean;
  onForceClose?: () => void;
}
export const WhatsNewModal = t0 => {
  const $ = _c(41);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    forceOpen,
    onForceClose
  } = t1;
  const {
    t
  } = useI18n();
  const [autoOpen, setAutoOpen] = useState(false);
  const open = forceOpen || autoOpen;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen !== WHATS_NEW_VERSION) {
        setAutoOpen(true);
      }
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== onForceClose) {
    t4 = () => {
      setAutoOpen(false);
      localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION);
      onForceClose?.();
    };
    $[4] = onForceClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleClose = t4;
  let t5;
  if ($[6] !== handleClose) {
    t5 = v => !v && handleClose();
    $[6] = handleClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <X className="size-4" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== handleClose) {
    t7 = <button type="button" onClick={handleClose} className="absolute top-4 right-4 rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">{t6}</button>;
    $[9] = handleClose;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t) {
    t8 = t("whatsNew.title");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <h2 className="text-lg font-semibold text-gray-900">{t8}</h2>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t("whatsNew.subtitle");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <p className="text-sm text-gray-500 mt-1">{t10}</p>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t7 || $[21] !== t9) {
    t12 = <div className="px-8 pt-8 pb-4 shrink-0">{t7}{t9}{t11}</div>;
    $[19] = t11;
    $[20] = t7;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== handleClose || $[24] !== t) {
    t13 = ALL_FEATURES.map(feature => <a key={feature.titleKey} href={feature.href} target="_blank" rel="noopener noreferrer" onClick={e => {
      e.preventDefault();
      window.open(feature.href, "_blank", "noopener,noreferrer");
      handleClose();
    }} className="flex items-center gap-3.5 py-3.5 group cursor-pointer"><div className="shrink-0 rounded-full bg-gray-100 p-2.5 text-gray-500 group-hover:bg-accent group-hover:text-primary transition-colors">{feature.icon}</div><div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900">{t(feature.titleKey as Parameters<typeof t>[0])}</p><p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-1">{t(feature.descriptionKey as Parameters<typeof t>[0])}</p></div><ArrowRight className="size-4 text-gray-300 group-hover:text-primary transition-colors shrink-0" /></a>);
    $[23] = handleClose;
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <div className="flex-1 overflow-y-auto px-8"><div className="divide-y divide-gray-100">{t13}</div></div>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t) {
    t15 = t("whatsNew.done");
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== handleClose || $[31] !== t15) {
    t16 = <div className="flex items-center justify-end px-8 py-4 shrink-0 border-t border-gray-100"><button type="button" onClick={handleClose} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">{t15}</button></div>;
    $[30] = handleClose;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t12 || $[34] !== t14 || $[35] !== t16) {
    t17 = <DialogContent className="max-w-[480px] max-h-[85vh] p-0 gap-0 flex flex-col rounded-2xl [&>button:last-child]:hidden">{t12}{t14}{t16}</DialogContent>;
    $[33] = t12;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== open || $[38] !== t17 || $[39] !== t5) {
    t18 = <Dialog open={open} onOpenChange={t5}>{t17}</Dialog>;
    $[37] = open;
    $[38] = t17;
    $[39] = t5;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};